import {
  MAX_PRODUCT_IMAGES,
  type PlatformProductUpdateRequest,
  type ProductDetail,
  type ProductTextList,
  type SetProductLogoRequest,
  type TenantMedia,
} from '@avrahami/contracts';
import {
  Alert,
  Button,
  Card,
  ConfirmDialog,
  FlyerPreview,
  Loading,
  NO_ERRORS,
  PageHeader,
  ProductListsCard,
  returnToPrevious,
  SelectField,
  TextAreaField,
  TextField,
  Thumbnail,
  useVersionedDraft,
  type FieldErrors,
} from '@avrahami/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useEffect, useRef, useState, type SyntheticEvent } from 'react';
import {
  keys,
  platformApi,
  tenantFlyerQuery,
  tenantProductQuery,
  tenantQuery,
} from '../api/platform';
import { hasCapability, sessionQuery } from '../api/session';
import { FormStatus, StaleNotice } from '../components/forms';
import { ImageAdder } from '../components/ImageAdder';
import { CopyStateBadge, ProductStatusBadge } from '../components/status';
import { he } from '../i18n/he';
import { COPY_LIMITS, copyCounter, copyImageUrl, copyRowState } from '../lib/ai-copy';
import { errorMessage } from '../lib/format';
import { mergeErrors } from '../lib/forms';
import {
  errorUnder,
  factRows,
  galleryItems,
  galleryRequest,
  isPageStatus,
  moveItem,
  PAGE_STATUSES,
  productPageDraft,
  productPageRequest,
  publishRefusal,
  type SpecDraft,
} from '../lib/product-page';

const words = he.productPage;

/**
 * One catalog product's page in one store, as the platform staff manage it (adr/0043): the same
 * page the store's own admin has (copy, status, the store's specification rows, images, the
 * manufacturer logo, the flyer), under `platform.ai.manage`. The copy is edited here and never
 * written again: there is no "write again" for the copy or the flyer on this page.
 */
export function TenantProductPage() {
  const { tenantId, productId } = useParams({
    from: '/app/tenants/$tenantId/products/$productId',
  });
  const session = useQuery(sessionQuery);
  const canManage = session.data !== undefined && hasCapability(session.data, 'platform.ai.manage');
  const product = useQuery({
    ...tenantProductQuery(tenantId, productId),
    enabled: canManage,
    // While the engine writes the product's lists, the page follows until they are written.
    refetchInterval: (query) => (query.state.data?.content.writing === true ? 4000 : false),
  });
  // Only for the store's name under the title: the page works without it.
  const tenant = useQuery(tenantQuery(tenantId));
  return (
    <>
      <Link
        to="/tenants/$tenantId/copy"
        params={{ tenantId }}
        onClick={returnToPrevious}
        className="mb-2 inline-block text-sm text-brand-700 hover:underline"
      >
        {words.back}
      </Link>
      {!canManage ? (
        session.isPending ? (
          <Loading />
        ) : (
          <Alert>{words.forbidden}</Alert>
        )
      ) : product.isPending ? (
        <Loading />
      ) : product.isError ? (
        <Alert>{errorMessage(product.error, { NOT_FOUND: words.notFound })}</Alert>
      ) : (
        <ProductEditor
          key={product.data.id}
          tenantId={tenantId}
          storeName={tenant.data?.tenant.displayName}
          product={product.data}
        />
      )}
    </>
  );
}

function ProductEditor({
  tenantId,
  storeName,
  product,
}: {
  tenantId: string;
  storeName: string | undefined;
  product: ProductDetail;
}) {
  const queryClient = useQueryClient();
  const versioned = useVersionedDraft(product, (value) => value.version, productPageDraft);
  // Typed into since the draft was last made from the server: a list is not given back then.
  const [touched, setTouched] = useState(false);
  const editor = {
    ...versioned,
    set: (change: Parameters<typeof versioned.set>[0]) => {
      setTouched(true);
      versioned.set(change);
    },
    reset: (from: ProductDetail) => {
      setTouched(false);
      versioned.reset(from);
    },
  };
  // The engine wrote the lists (no new version): an untouched page shows them.
  const wasWriting = useRef(product.content.writing);
  useEffect(() => {
    if (wasWriting.current && !product.content.writing && !touched) editor.reset(product);
    wasWriting.current = product.content.writing;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs when the writing ends
  }, [product.content.writing]);
  const [localErrors, setLocalErrors] = useState<FieldErrors>(NO_ERRORS);
  /** The product as the server answered: the page shows it, and the copy screen reads again. */
  const show = async (updated: ProductDetail) => {
    queryClient.setQueryData(keys.tenantProduct(tenantId, product.id), updated);
    await queryClient.invalidateQueries({ queryKey: keys.tenantCopySummary(tenantId) });
    await queryClient.invalidateQueries({ queryKey: keys.tenantCopyItems(tenantId) });
    await queryClient.invalidateQueries({ queryKey: keys.tenantFlyer(tenantId, product.id) });
  };
  const reread = () =>
    queryClient
      .query({ ...tenantProductQuery(tenantId, product.id), staleTime: 0 })
      // A failed read shows on the page itself (the product query's error).
      .then(undefined, () => undefined);

  const save = useMutation({
    mutationFn: (body: PlatformProductUpdateRequest) =>
      platformApi.updateTenantProduct(tenantId, product.id, body),
    onSuccess: async (updated) => {
      editor.reset(updated);
      await show(updated);
    },
  });
  const approve = useMutation({
    mutationFn: () =>
      platformApi.approveAiCopy(tenantId, { selection: 'SELECTED', productIds: [product.id] }),
    onSuccess: async () => {
      await reread();
      await queryClient.invalidateQueries({ queryKey: keys.tenantCopy(tenantId) });
    },
  });
  const [confirming, setConfirming] = useState<ProductTextList | null>(null);
  const restore = useMutation({
    mutationFn: (list: ProductTextList) =>
      platformApi.restoreTenantProductList(tenantId, product.id, {
        version: editor.baseVersion,
        list,
      }),
    onSuccess: async (updated) => {
      setConfirming(null);
      save.reset();
      editor.reset(updated);
      await show(updated);
    },
  });
  const reload = () => {
    save.reset();
    setLocalErrors(NO_ERRORS);
    void reread().then((latest) => {
      if (latest !== undefined) editor.reset(latest);
    });
  };

  const refusal = publishRefusal(save.error);
  const errors: FieldErrors = {
    ...mergeErrors(localErrors, save.error),
    ...(refusal === null ? {} : { status: refusal }),
  };
  const onSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    // The version the draft was made from, never a newer one the server returned since.
    const parsed = productPageRequest(editor.draft, editor.baseVersion);
    if (!parsed.ok) {
      setLocalErrors(parsed.errors);
      return;
    }
    setLocalErrors(NO_ERRORS);
    save.mutate(parsed.value);
  };
  const copyField = (name: 'title' | 'description' | 'seoTitle' | 'seoDescription') => ({
    label: he.aiCopy.fields[name],
    name,
    maxLength: COPY_LIMITS[name],
    value: editor.draft[name],
    hint: copyCounter(editor.draft, name),
    error: errors[`content.${name}`],
  });
  const state = copyRowState({ contentState: product.content.state, job: null });

  return (
    <>
      <PageHeader
        title={product.content.title ?? product.facts.name}
        actions={
          <ProductStatusBadge
            status={product.status}
            hiddenFromShoppers={product.hiddenFromShoppers}
          />
        }
      />
      <div className="flex flex-col gap-6" data-testid="product-page">
        <div className="flex flex-col gap-2">
          {storeName === undefined ? null : (
            <p className="text-sm font-medium text-slate-700">{storeName}</p>
          )}
          <p className="text-sm text-slate-600">{words.intro}</p>
          <p className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
            <span>{words.contentState}:</span>
            <CopyStateBadge state={state} />
            {product.content.state === 'AI_GENERATED' ? (
              <Button
                data-testid="product-approve"
                busy={approve.isPending}
                onClick={() => {
                  approve.mutate();
                }}
              >
                {words.approve}
              </Button>
            ) : null}
          </p>
          {approve.isError ? <Alert>{errorMessage(approve.error)}</Alert> : null}
          {approve.isSuccess ? <Alert tone="success">{words.approved}</Alert> : null}
        </div>
        {product.publishBlockers.length === 0 ? null : (
          <Alert tone="info">
            <span data-testid="product-blockers">
              {words.blockers}{' '}
              {product.publishBlockers.map((blocker) => he.publishBlockers[blocker]).join(', ')}
            </span>
          </Alert>
        )}

        <form onSubmit={onSubmit} noValidate data-testid="product-form">
          <div className="flex flex-col gap-6">
            <StaleNotice stale={editor.stale && !save.isError} onReload={reload} />
            <Card title={words.status}>
              <div className="flex max-w-xs flex-col gap-2">
                <SelectField
                  label={words.status}
                  name="status"
                  data-testid="product-status-select"
                  value={editor.draft.status}
                  error={errors['status']}
                  onChange={(event) => {
                    if (isPageStatus(event.target.value)) {
                      editor.set({ status: event.target.value });
                    }
                  }}
                >
                  {PAGE_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {he.productStatus[status]}
                    </option>
                  ))}
                </SelectField>
                <p className="text-xs text-slate-500">{words.statusHint}</p>
              </div>
            </Card>

            <Card title={words.content}>
              <div className="flex flex-col gap-4" data-testid="product-content">
                <TextField
                  {...copyField('title')}
                  onChange={(event) => {
                    editor.set({ title: event.target.value });
                  }}
                />
                <TextAreaField
                  {...copyField('description')}
                  rows={10}
                  onChange={(event) => {
                    editor.set({ description: event.target.value });
                  }}
                />
                <TextField
                  {...copyField('seoTitle')}
                  onChange={(event) => {
                    editor.set({ seoTitle: event.target.value });
                  }}
                />
                <TextAreaField
                  {...copyField('seoDescription')}
                  rows={3}
                  onChange={(event) => {
                    editor.set({ seoDescription: event.target.value });
                  }}
                />
              </div>
            </Card>

            <ProductListsCard
              words={words.lists}
              draft={editor.draft}
              set={editor.set}
              errors={errors}
              content={product.content}
              catalog
              restore={{
                words: words.restoreList,
                unsaved: touched,
                restoring: restore.isPending ? restore.variables : null,
                onRestore: (list) => {
                  restore.reset();
                  setConfirming(list);
                },
              }}
            />

            <Card title={words.facts}>
              <div className="flex flex-col gap-4">
                <p className="text-sm text-slate-600">{words.factsLocked}</p>
                <dl
                  className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 text-sm"
                  data-testid="product-facts"
                >
                  {factRows(product).map((row, index) => (
                    <div key={`${row.label}-${String(index)}`} className="contents">
                      <dt className="text-slate-500">{row.label}</dt>
                      <dd className="break-words">{row.value}</dd>
                    </div>
                  ))}
                </dl>
                <SpecRows
                  rows={editor.draft.extraSpecs}
                  error={errorUnder(errors, 'facts.extraSpecs')}
                  onChange={(extraSpecs) => {
                    editor.set({ extraSpecs });
                  }}
                />
              </div>
            </Card>

            <FormStatus
              error={save.error}
              localInvalid={Object.keys(localErrors).length > 0}
              saved={save.isSuccess}
              {...(refusal === null ? {} : { overrides: { CONFLICT: refusal } })}
              onReload={reload}
            />
            <div>
              <Button type="submit" busy={save.isPending} data-testid="product-save">
                {he.common.save}
              </Button>
            </div>
          </div>
        </form>

        <ConfirmDialog
          open={confirming !== null}
          title={words.restoreList.label}
          confirmLabel={words.restoreList.label}
          confirmTestId="restore-list-confirm"
          busy={restore.isPending}
          onConfirm={() => {
            if (confirming !== null) restore.mutate(confirming);
          }}
          onClose={() => {
            setConfirming(null);
          }}
        >
          <p>
            {words.restoreConfirm.replace(
              '{list}',
              confirming === null ? '' : words.lists.names[confirming],
            )}
          </p>
          {restore.error === null ? null : (
            <div className="mt-3">
              <Alert>{errorMessage(restore.error)}</Alert>
            </div>
          )}
        </ConfirmDialog>

        <Gallery tenantId={tenantId} product={product} onSaved={show} onReload={reread} />
        <LogoCard tenantId={tenantId} product={product} onSaved={show} onReload={reread} />
        <FlyerCard tenantId={tenantId} productId={product.id} />
      </div>
    </>
  );
}

/** The store's own specification rows of a catalog product, with add and remove. */
function SpecRows({
  rows,
  onChange,
  error,
}: {
  rows: readonly SpecDraft[];
  onChange: (rows: SpecDraft[]) => void;
  error: string | undefined;
}) {
  const update = (index: number, change: Partial<SpecDraft>) => {
    onChange(rows.map((row, at) => (at === index ? { ...row, ...change } : row)));
  };
  return (
    <fieldset className="flex flex-col gap-2" data-testid="product-extra-specs">
      <legend className="mb-1 text-sm font-medium text-slate-700">{words.extraSpecs}</legend>
      {rows.map((row, index) => (
        <div key={index} className="flex flex-wrap items-end gap-2">
          <TextField
            label={`${words.specLabel} ${String(index + 1)}`}
            value={row.label}
            maxLength={120}
            className="w-48"
            onChange={(event) => {
              update(index, { label: event.target.value });
            }}
          />
          <TextField
            label={`${words.specValue} ${String(index + 1)}`}
            value={row.value}
            maxLength={500}
            className="min-w-48 flex-1"
            onChange={(event) => {
              update(index, { value: event.target.value });
            }}
          />
          <Button
            tone="secondary"
            onClick={() => {
              onChange(rows.filter((_row, at) => at !== index));
            }}
          >
            {words.removeSpec}
          </Button>
        </div>
      ))}
      <div>
        <Button
          tone="secondary"
          onClick={() => {
            onChange([...rows, { label: '', value: '' }]);
          }}
        >
          {words.addSpec}
        </Button>
      </div>
      {error === undefined ? null : (
        <p role="alert" className="text-xs text-red-600">
          {error}
        </p>
      )}
    </fieldset>
  );
}

interface PartProps {
  tenantId: string;
  product: ProductDetail;
  onSaved: (updated: ProductDetail) => Promise<void>;
  onReload: () => Promise<ProductDetail | undefined>;
}

/** The product's images: order, alt texts, remove, and new uploads; saved as one replacement. */
function Gallery({ tenantId, product, onSaved, onReload }: PartProps) {
  // Unsaved changes here survive a save of the details above (which moves the version): the
  // gallery then says the product changed, and its own save answers CONFLICT rather than
  // overwriting. With nothing edited here, it simply follows the product.
  const gallery = useVersionedDraft(
    product,
    (value) => value.version,
    (value) => ({ items: galleryItems(value.media) }),
  );
  const items = gallery.draft.items;
  const setItems = (change: (current: typeof items) => typeof items) => {
    gallery.update((draft) => ({ items: change(draft.items) }));
  };
  const addUpload = (media: TenantMedia) => {
    setItems((current) => [
      ...current,
      { key: media.id, kind: 'UPLOAD', mediaId: media.id, file: media.file, altText: '' },
    ]);
  };
  const save = useMutation({
    mutationFn: () =>
      platformApi.replaceTenantProductMedia(
        tenantId,
        product.id,
        galleryRequest(items, gallery.baseVersion),
      ),
    onSuccess: async (updated) => {
      gallery.reset(updated);
      await onSaved(updated);
    },
  });
  const reload = () => {
    save.reset();
    void onReload().then((latest) => {
      if (latest !== undefined) gallery.reset(latest);
    });
  };
  const full = items.length >= MAX_PRODUCT_IMAGES;

  return (
    <Card title={words.gallery}>
      <div className="flex flex-col gap-4" data-testid="product-gallery">
        {items.length === 0 ? (
          <p className="text-sm text-slate-500">{words.galleryEmpty}</p>
        ) : (
          <ol className="flex flex-col gap-3">
            {items.map((item, index) => (
              <li
                key={item.key}
                data-testid="gallery-item"
                data-kind={item.kind}
                className="flex flex-wrap items-end gap-3 rounded-md p-2 ring-1 ring-slate-200"
              >
                <Thumbnail
                  src={copyImageUrl(tenantId, item.file)}
                  alt={item.altText || `${words.image} ${String(index + 1)}`}
                  size={64}
                />
                <TextField
                  label={`${words.altText} ${String(index + 1)}`}
                  value={item.altText}
                  maxLength={200}
                  className="min-w-48 flex-1"
                  onChange={(event) => {
                    const altText = event.target.value;
                    setItems((current) =>
                      current.map((entry) =>
                        entry.key === item.key ? { ...entry, altText } : entry,
                      ),
                    );
                  }}
                />
                <div className="flex gap-1">
                  <Button
                    tone="secondary"
                    aria-label={`${words.moveUp} ${String(index + 1)}`}
                    disabled={index === 0}
                    onClick={() => {
                      setItems((current) => moveItem(current, index, -1));
                    }}
                  >
                    ↑
                  </Button>
                  <Button
                    tone="secondary"
                    aria-label={`${words.moveDown} ${String(index + 1)}`}
                    disabled={index === items.length - 1}
                    onClick={() => {
                      setItems((current) => moveItem(current, index, 1));
                    }}
                  >
                    ↓
                  </Button>
                  <Button
                    tone="secondary"
                    aria-label={`${words.remove} ${String(index + 1)}`}
                    onClick={() => {
                      setItems((current) => current.filter((entry) => entry.key !== item.key));
                    }}
                  >
                    {words.remove}
                  </Button>
                </div>
              </li>
            ))}
          </ol>
        )}
        {full ? (
          <p className="text-xs text-slate-500">{words.galleryFull}</p>
        ) : (
          <ImageAdder
            tenantId={tenantId}
            label={words.addImage}
            onUploaded={addUpload}
            limit={MAX_PRODUCT_IMAGES - items.length}
            testId="gallery-upload"
          />
        )}
        <StaleNotice stale={gallery.stale} onReload={reload} />
        <FormStatus
          error={save.error}
          localInvalid={false}
          saved={save.isSuccess}
          onReload={reload}
        />
        <div>
          <Button
            busy={save.isPending}
            data-testid="gallery-save"
            onClick={() => {
              save.mutate();
            }}
          >
            {words.saveGallery}
          </Button>
        </div>
      </div>
    </Card>
  );
}

/**
 * The manufacturer logo the store shows on the product (adr/0041): the catalog's, one uploaded
 * instead, or none. Every choice is saved at once.
 */
function LogoCard({ tenantId, product, onSaved, onReload }: PartProps) {
  const save = useMutation({
    mutationFn: (logo: SetProductLogoRequest['logo']) =>
      platformApi.setTenantProductLogo(tenantId, product.id, { version: product.version, logo }),
    onSuccess: onSaved,
  });
  const reload = () => {
    save.reset();
    void onReload();
  };
  const { logo } = product;
  const note =
    logo.source === 'CATALOG' && logo.file === null
      ? words.logo.catalogNone
      : words.logo.sources[logo.source];
  return (
    <Card title={words.logo.title}>
      <div className="flex flex-col gap-3" data-testid="product-logo" data-source={logo.source}>
        <p className="text-sm text-slate-600">{words.logo.hint}</p>
        <div className="flex flex-wrap items-center gap-3">
          {logo.file === null ? (
            <span className="text-xs text-slate-500">{words.logo.none}</span>
          ) : (
            <Thumbnail src={copyImageUrl(tenantId, logo.file)} alt={words.logo.alt} size={80} />
          )}
          <p className="min-w-0 flex-1 text-sm text-slate-700" data-testid="product-logo-note">
            {note}
          </p>
        </div>
        <ImageAdder
          tenantId={tenantId}
          label={logo.file === null ? words.logo.upload : words.logo.replace}
          onUploaded={(media) => {
            save.mutate({ source: 'UPLOAD', mediaId: media.id });
          }}
          testId="product-logo-upload"
        />
        <div className="flex flex-wrap gap-2">
          {logo.file === null ? null : (
            <Button
              tone="secondary"
              busy={save.isPending && save.variables.source === 'NONE'}
              data-testid="product-logo-remove"
              onClick={() => {
                save.mutate({ source: 'NONE' });
              }}
            >
              {words.logo.remove}
            </Button>
          )}
          {logo.source === 'CATALOG' ? null : (
            <Button
              tone="secondary"
              busy={save.isPending && save.variables.source === 'CATALOG'}
              data-testid="product-logo-catalog"
              onClick={() => {
                save.mutate({ source: 'CATALOG' });
              }}
            >
              {words.logo.backToCatalog}
            </Button>
          )}
        </div>
        <FormStatus
          error={save.error}
          localInvalid={false}
          saved={save.isSuccess}
          onReload={reload}
        />
      </div>
    </Card>
  );
}

/** The product's flyer as the store gets it (docs/FLYERS.md), to look at and download only. */
function FlyerCard({ tenantId, productId }: { tenantId: string; productId: string }) {
  const flyer = useQuery(tenantFlyerQuery(tenantId, productId));
  const flyerWords = he.aiCopy.flyers;
  return (
    <Card title={words.flyer}>
      <div className="flex flex-col gap-3" data-testid="product-flyer">
        <p className="text-sm text-slate-600">{words.flyerIntro}</p>
        {flyer.isPending ? <Loading /> : null}
        {flyer.isError ? <Alert>{errorMessage(flyer.error)}</Alert> : null}
        {flyer.data === undefined ? null : (
          <>
            <FlyerPreview
              flyer={flyer.data}
              mediaUrl={(file) => copyImageUrl(tenantId, file)}
              words={flyerWords}
            />
            {flyer.data.price === null ? (
              <p className="text-xs text-slate-500">{flyerWords.noPrice}</p>
            ) : null}
          </>
        )}
      </div>
    </Card>
  );
}
